import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { BillingPlanTier } from '@core/billing-types';
import { FormControlsOf, FormGroupOf } from '@core/common-legacy';
import { IconName } from '@core/user-interface-legacy';
import {
  AddFollowersActionData,
  AddNoteActionData,
  AddSourceTagsActionData,
  AddTagsActionData,
  AddTaskActionData,
  AddTeamMembersActionData,
  AssignActionData,
  AutomationActionType,
  AutomationConditionType,
  AutomationTriggerDataValue,
  AutomationTriggerType,
  CancelScheduledEventActionData,
  JobChangeStatusData,
  RequestEvaluationActionData,
  SendEmailActionData,
  ShareActionData,
  SubmitToJobBoardsActionData,
  TriggerDataSelectOption,
  UnassignActionData,
  UnshareActionData,
  AutomationConditionDataValue,
  TriggerConditionDataSelectOption,
} from '@core/workflow-automations-types';

// Automation form
export type AutomationForm = FormGroup<{
  trigger: FormGroup<AutomationTriggerForm>;
  conditions: FormArray<AutomationConditionForm>;
  actions: FormArray;
}>;

// Triggers
export type AutomationTriggerForm = {
  type: FormControl<AutomationTriggerType | null>;
  data: FormGroupOf<Record<string, AutomationTriggerDataValue>>;
};

export type TriggerDataOptions = TriggerDataSelectOption[];
export type TriggerOptionData = { label: string; name: string; options?: TriggerDataOptions };

export type ActionOption = {
  type: AutomationActionType;
  label: string;
  icon: IconName;
  enableActionTooltip?: string;
  editActionTooltip?: string;
  recommended?: boolean;
  available: boolean;
  entitled: boolean;
  entitledFrom: BillingPlanTier | null;
};

// Conditions
export type AutomationConditionForm = FormGroup<{
  type: FormControl<AutomationConditionType | null>;
  data: FormGroupOf<Record<string, AutomationConditionDataValue>>;
}>;

export type TriggerConditionDataOptions = TriggerConditionDataSelectOption[];

export type ConditionOption = {
  type: AutomationConditionType;
  label: string;
  options: Array<{
    label: string;
    name: string;
    options?: TriggerConditionDataOptions;
  }>;
};

// Actions
export type AddTagsActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAddTags | AutomationActionType.JobAddTags>;
  data: FormGroupOf<AddTagsActionData>;
}>;
export type AddSourceTagsActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAddSourceTags>;
  data: FormGroupOf<AddSourceTagsActionData>;
}>;
export type AddTaskActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAddTask>;
  data: FormGroupOf<AddTaskActionData>;
}>;
export type AddFollowersActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAddFollowers>;
  data: FormGroupOf<AddFollowersActionData>;
}>;
export type AddNoteActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAddNote | AutomationActionType.JobAddNote>;
  data: FormGroupOf<AddNoteActionData>;
}>;
export type RequestEvaluationActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateEvaluationRequest>;
  data: FormGroupOf<RequestEvaluationActionData>;
}>;
export type AssignActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateAssignTo>;
  data: FormGroupOf<Pick<AssignActionData, 'offerIds' | 'talentPoolIds'>>;
}>;
export type UnassignActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateUnassignFromJob>;
  data: FormGroupOf<Pick<UnassignActionData, 'offerIds' | 'talentPoolIds' | 'includeSelf'>>;
}>;
export type SendEmailActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateSendEmail>;
  data: FormGroupOf<SendEmailActionData>;
}>;
export type CancelScheduledEventActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateCancelScheduledEvents>;
  data: FormGroupOf<CancelScheduledEventActionData>;
}>;
export type AddTeamMembersActionForm = FormGroup<{
  type: FormControl<AutomationActionType.JobAddTeamMembers>;
  data: FormGroupOf<AddTeamMembersActionData>;
}>;
export type JobChangeStatusActionForm = FormGroup<{
  type: FormControl<AutomationActionType.JobChangeStatus>;
  data: FormGroupOf<JobChangeStatusData>;
}>;
export type SendToTellentHrActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateSendToTellentHR>;
  data: FormGroupOf<null>;
}>;
export type SubmitToJobBoardsActionForm = FormGroup<{
  type: FormControl<AutomationActionType.JobSubmitToJobBoards>;
  data: FormGroup<FormControlsOf<SubmitToJobBoardsActionData>>;
}>;
export type ShareActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateShare>;
  data: FormGroupOf<ShareActionData>;
}>;
export type UnshareActionForm = FormGroup<{
  type: FormControl<AutomationActionType.CandidateUnshare>;
  data: FormGroupOf<UnshareActionData>;
}>;

export type AutomationActionForm = { uuid?: string } & (
  | AddTagsActionForm
  | AddSourceTagsActionForm
  | AddTaskActionForm
  | AddFollowersActionForm
  | AddNoteActionForm
  | RequestEvaluationActionForm
  | AssignActionForm
  | UnassignActionForm
  | SendEmailActionForm
  | AddTeamMembersActionForm
  | JobChangeStatusActionForm
  | CancelScheduledEventActionForm
  | SendToTellentHrActionForm
  | SubmitToJobBoardsActionForm
  | ShareActionForm
  | UnshareActionForm
);

export enum RecipientType {
  Candidate = 'candidate',
  Recruiter = 'recruiter',
  HiringManager = 'hiringManager',
}
